import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import {
  CheckCircle2, Circle, ArrowRight, Sparkles, X, GraduationCap,
} from 'lucide-react';

/**
 * 5-step onboarding checklist that surfaces on the Dashboard until the user
 * has completed every item. Each row links to the matching tutorial OR the
 * feature itself, depending on whether the user has done it yet.
 *
 * Dismiss is remembered in localStorage so we don't nag returning users.
 */
const STORAGE_KEY = 'socialgen:onboarding:dismissed';

const STEPS = [
  {
    id: 'connect',
    label: 'Connect a social account',
    help: 'Link LinkedIn, Facebook, Instagram, X, or YouTube so posts can publish.',
    tutorial: 'connect-accounts',
    href: '/connections',
    ctaLabel: 'Connect',
  },
  {
    id: 'generate',
    label: 'Generate your first post',
    help: 'Use the single-post Generator for a quick draft in under 30 seconds.',
    tutorial: 'generate-post',
    href: '/generate',
    ctaLabel: 'Generate',
  },
  {
    id: 'campaign',
    label: 'Run the AI Post Manager',
    help: 'Draft a whole multi-platform campaign in one click.',
    tutorial: 'ai-post-manager',
    href: '/ai-manager',
    ctaLabel: 'Open',
  },
  {
    id: 'schedule',
    label: 'Schedule a post',
    help: 'Queue a post for a specific time — the worker publishes for you.',
    tutorial: 'schedule',
    href: '/posts',
    ctaLabel: 'Schedule',
  },
  {
    id: 'client',
    label: 'Create a Client (optional)',
    help: 'Skip if you run one brand. Great for agencies managing many.',
    tutorial: 'clients',
    href: '/clients',
    ctaLabel: 'Add client',
    optional: true,
  },
];

export const OnboardingChecklist = () => {
  const { api } = useAuth();
  const [state, setState] = useState({
    connect: false,
    generate: false,
    campaign: false,
    schedule: false,
    client: false,
  });
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        const [connRes, postsRes, campaignsRes, clientsRes] = await Promise.all([
          api.get('/connections').catch(() => ({ data: { connections: [] } })),
          api.get('/posts?limit=100').catch(() => ({ data: { posts: [] } })),
          api.get('/campaigns').catch(() => ({ data: { campaigns: [] } })),
          api.get('/clients').catch(() => ({ data: { clients: [] } })),
        ]);
        if (!alive) return;

        const connections = connRes.data?.connections || [];
        const posts = postsRes.data?.posts || [];
        const campaigns = campaignsRes.data?.campaigns || [];
        const clients = clientsRes.data?.clients || [];

        setState({
          connect: connections.some((c) => c.connected),
          generate: posts.length > 0,
          campaign: campaigns.length > 0,
          schedule: posts.some(
            (p) => p.status === 'scheduled' || p.status === 'published'
          ),
          client: clients.length > 0,
        });
      } catch (err) {
        // swallow — checklist stays empty, user can still dismiss
        console.error('Onboarding fetch failed', err);
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [api]);

  const doneCount = STEPS.filter((s) => state[s.id]).length;
  const pct = Math.round((doneCount / STEPS.length) * 100);
  const allDone = doneCount === STEPS.length;

  const handleDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  if (dismissed || allDone || loading) return null;

  return (
    <Card
      className="border-indigo-100 bg-gradient-to-br from-indigo-50/70 via-white to-white"
      data-testid="onboarding-checklist"
    >
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-600 text-white flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-zinc-900 tracking-tight">
                Get started with PostToSocial
              </h3>
              <p className="text-sm text-zinc-600 mt-0.5">
                {doneCount} of {STEPS.length} complete — new to the app?{' '}
                <Link
                  to="/tutorials"
                  className="text-indigo-600 hover:text-indigo-700 font-medium inline-flex items-center gap-1"
                  data-testid="onboarding-browse-tutorials"
                >
                  Browse tutorials
                  <GraduationCap className="w-3.5 h-3.5" />
                </Link>
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDismiss}
            className="text-zinc-400 hover:text-zinc-700 -mr-2 -mt-1"
            data-testid="onboarding-dismiss"
            aria-label="Dismiss onboarding checklist"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <Progress value={pct} className="mt-4 h-2" />

        <ul className="mt-5 space-y-2.5">
          {STEPS.map((step) => {
            const done = state[step.id];
            return (
              <li
                key={step.id}
                className={`flex items-start sm:items-center gap-3 p-3 rounded-lg border transition-colors ${
                  done
                    ? 'bg-emerald-50/50 border-emerald-100'
                    : 'bg-white border-zinc-200 hover:border-indigo-200'
                }`}
                data-testid={`onboarding-step-${step.id}`}
              >
                {done ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5 sm:mt-0" />
                ) : (
                  <Circle className="w-5 h-5 text-zinc-300 shrink-0 mt-0.5 sm:mt-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p
                    className={`font-medium ${
                      done ? 'text-emerald-900 line-through decoration-emerald-300' : 'text-zinc-900'
                    }`}
                  >
                    {step.label}
                    {step.optional && !done && (
                      <span className="ml-2 text-xs font-normal text-zinc-400">
                        (optional)
                      </span>
                    )}
                  </p>
                  {!done && (
                    <p className="text-xs text-zinc-500 mt-0.5">{step.help}</p>
                  )}
                </div>
                {!done && (
                  <div className="flex items-center gap-2 shrink-0">
                    <Link
                      to={`/tutorials?id=${step.tutorial}`}
                      className="text-xs text-zinc-500 hover:text-indigo-600 hidden sm:inline"
                      data-testid={`onboarding-learn-${step.id}`}
                    >
                      Learn
                    </Link>
                    <Link to={step.href}>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                        data-testid={`onboarding-cta-${step.id}`}
                      >
                        {step.ctaLabel}
                        <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    </Link>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
};

export default OnboardingChecklist;
